import React, { useState, ReactNode, MouseEvent, DragEvent, useEffect } from "react";
import { Button, Collapse, Dropdown } from "react-bootstrap";
import { ActionHandlerFunction } from "@preveil-api";
import { MailTreeIcon, UIManagerActionTypes, useAppSelector, DragEventTypes, DragEventType, TreeItem } from "src/common";
import { Icon, TreeFolderIcon, CreateFolderRow } from "src/components";

type AllProps = {
  folder: TreeItem;
  children: ReactNode;
  readonly?: boolean;
  active?: boolean;
  fetching_children?: boolean;
  drive?: boolean;
  drag_events?: DragEventTypes[];
  empty_tooltip?: string;
  show_create_folder?: boolean;
  creating_folder_id?: string;
  onSetCreating?: (id: string | undefined) => void;
  handleAction: ActionHandlerFunction;
};

function TreeFolder(props: AllProps) {
  const { folder, active, children, readonly, drive, drag_events, show_create_folder, creating_folder_id, onSetCreating, handleAction } = props;
  const expanded = useAppSelector((state) => state.mail.expanded);
  const is_creating = !!show_create_folder && creating_folder_id === folder.id;
  const [open, setOpen] = useState<boolean>(expanded.includes(folder?.id || "") || !!folder.expanded || false);

  useEffect(() => {
    if (!!folder.expanded && !!folder.children_loading) {
      handleOnClickFolder();
    }
  }, []);

  // Description: Conditionally add drag events to the tree node
  const drag_props = !!drag_events ? {
    onDragOver: (event: DragEvent) => (drag_events.includes(DragEventType.DRAG_OVER) && handleAction({ actionType: UIManagerActionTypes.OnDragOver, params: { event, folder } })),
    onDrop: (event: DragEvent) => (drag_events.includes(DragEventType.DROP) && handleAction({ actionType: UIManagerActionTypes.OnDrop, params: { event, folder } })),
    onDragLeave: (event: DragEvent) => (drag_events.includes(DragEventType.DRAG_LEAVE) && handleAction({ actionType: UIManagerActionTypes.OnDragLeave, params: { event, folder } }))
    // NOTE: Add more DragEvent types as needed
    // onDragEnd: (event: DragEvent) => (drag_events.includes(DragEventType.DRAG_END) && handleAction({ actionType: UIManagerActionTypes.OnDragEnd, params: { event, folder } }))
  } : undefined;

  // Description: Handles click to icon
  function handleOnClickFolder(e?: MouseEvent<HTMLSpanElement>) {
    if (!!e) {
      e.stopPropagation();
      e.preventDefault();
    }
    // NOTE: If no children redirect bubble parent click to handle action TreeNodeActiveState
    if (!!folder.children) {
      setOpen(!open);
      !drive && handleAction({ actionType: UIManagerActionTypes.TreeNodeActiveState, params: { id: folder.id, expanded: !open } });
    } else if (folder.children_loading) {
      !!e && setOpen(!open);
      handleAction({ actionType: UIManagerActionTypes.FetchTreeNodeChildren, params: folder });
    } else {
      handleAction({ actionType: UIManagerActionTypes.TreeNodeClick, params: folder });
    }
  }

  function FolderActions() {
    return <Dropdown as="span" className="folder-actions">
      <Dropdown.Toggle variant="light" size="sm" >
        <Icon className="ficon-more-vertical" />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item
          aria-label="Create Subfolder"
          onClick={() => {
            // Open parent
            !open && setOpen(true);
            handleAction({ actionType: UIManagerActionTypes.TreeNodeActiveState, params: { id: folder.id, expanded: !open } });
            handleAction({ actionType: UIManagerActionTypes.TreeNodeAddChild, params: folder });
          }}>
          <Icon className="pv-icon-new-folder" />
          Create Subfolder
        </Dropdown.Item>
        <Dropdown.Item aria-label="Rename" onClick={() => handleAction({ actionType: UIManagerActionTypes.TreeNodeRename, params: folder })}>
          <Icon className="pv-icon-compose" />
          Rename
        </Dropdown.Item>
        <Dropdown.Item aria-label="Delete" onClick={() => handleAction({ actionType: UIManagerActionTypes.TreeNodeDelete, params: folder })}>
          <Icon className="pv-icon-trash" />
          Delete
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>;
  }

  return <div data-tooltip-id="pv-tooltip" data-tooltip-content={folder.disabled && drive ? "The item(s) you want to move are already under this folder" : ""} className={`folder-item${folder.disabled ? " disabled" : ""}`}>
    <div id={folder.id}
      {...drag_props}
      className={`folder-label ${folder.active || active ? " active" : ""}`}>
      <span className={`folder-icon${!folder.children && !folder.children_loading ? " no-children" : ""}`}
        onClick={handleOnClickFolder}>
        {
          !!drive ? <TreeFolderIcon {...props} open={open} /> :
            <Icon className={open ? MailTreeIcon.opened : MailTreeIcon.closed} />
        }
      </span>
      <span className="folder-name"
        onClick={() => !folder.disabled && handleAction({ actionType: UIManagerActionTypes.TreeNodeClick, params: folder })}>{folder.name}</span>
      {
        !readonly && <FolderActions />
      }
      {
        !!show_create_folder &&
        <Button
          variant="icon"
          size="sm"
          className="add-folder-btn"
          aria-label="Add Folder"
          onMouseDown={(_e: MouseEvent<HTMLButtonElement>) => {
            setOpen(true);
            onSetCreating?.(folder.id);
            // NOTE: Do not fetch children if branch is already populated
            !folder.children && handleAction({ actionType: UIManagerActionTypes.FetchTreeNodeChildren, params: folder });
          }}
        >
          <Icon className="ficon-folder-plus" />
        </Button>
      }
    </div>
    {
      !!is_creating &&
      <CreateFolderRow
        folder={folder}
        onCancel={() => onSetCreating?.(undefined)}
        handleAction={handleAction}
      />
    }
    {
      !!folder.children &&
      <Collapse in={open} className="folder-children">
        <div>
          {children}
        </div>
      </Collapse>
    }
  </div >;
}

export default React.memo(TreeFolder);
